import CheckIcon from '@eg/elements/Icons/CheckIcon';
import { LoaderIcon } from '@eg/elements/components/Icons';
import { FC, useState } from 'react';
import AemFragment from '../../../../components/AemFragment/AemFragment';
import { AppLoaderBlack } from '../../../../components/AppLoader/AppLoaderBlack';
import { ErrorModal } from '../../../../components/ErrorModal/ErrorModal';
import Footer, { AuxButton } from '../../../../components/Footer';
import { Headline } from '../../../../components/Headline/Headline';
import Text from '../../../../components/Text';
import { CONFIG_BACKEND_API_PREFIX, CONFIG_BACKEND_BASE_URL } from '../../../../config';
import * as downloadPDFUtils from '../../../../helpers/downloadPDF.utils';
import { NavigationAction } from '../../../../routing/StateMachineTypes';
import { Tracker } from '../../../../tracking/tracker';
import { TrackingElementDUUPage } from '../../../../tracking/tracking.types';
import { AemFragmentCatalog } from '../../../../types/AemFragmentCatalog';
import { SharedSubmitCallBack } from '../../../../types/callbacks/sharedSubmitCallBack';
import '../IDDDownloadPdf/IddDownloadPdf.css';

const tracker = Tracker.getInstance();
interface DuuDownloadPdfSubmitProps extends SharedSubmitCallBack<string> {
  onNavigateNoSubmit: (navigationRequest: NavigationAction) => void;
  onSubmit: (value: string, navigationRequest: NavigationAction) => void;
  businessId: string;
  onError: (e: Error) => void;
  pdfUrlDownloaded: string | undefined;
  trackClickEvent: (clickedElement: TrackingElementDUUPage) => void;
}

export const DuuDownloadPdfSubmit: FC<DuuDownloadPdfSubmitProps> = ({
  businessId,
  onError,
  onSubmit,
  onNavigateNoSubmit,
  trackClickEvent,
  pdfUrlDownloaded,
}) => {
  const [isSubmitOrderButtonDisabled, setIsSubmitOrderButtonDisabled] = useState(!pdfUrlDownloaded);
  const [isPdfDownloaded, setIsPdfDownloaded] = useState(!!pdfUrlDownloaded);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);

  const onDownloadPdfClick = async (): Promise<void> => {
    trackClickEvent(TrackingElementDUUPage.BUTTON_UNTERLAGEN_HERUNTERLADEN);
    setOpenErrorModal(false);

    try {
      setIsLoadingPdf(true);

      const dynamicPdfUrl = `${CONFIG_BACKEND_BASE_URL}${CONFIG_BACKEND_API_PREFIX}/document/${businessId}/signed`;
      await downloadPDFUtils.openPDFByBlob(dynamicPdfUrl, 'Vertragsdokumente_RLV_DIVE');

      setIsPdfDownloaded(true);
      setIsSubmitOrderButtonDisabled(false);
    } catch (err) {
      onError(err as Error);
      setOpenErrorModal(true);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  return (
    <div className="downloadpdf content-container">
      <Headline
        text={'Schließen Sie Ihren Vertrag jetzt online ab.'}
        subline={
          'Mit dem Klick auf „Online beantragen“ werden Ihre Vertragsdaten verbindlich an die ERGO Vorsorge Lebensversicherung AG übertragen.'
        }
      />

      <Text styles="bold subheader spaced">Unterschriebene Vertragsunterlagen herunterladen</Text>
      <Text>
        Bitte speichern Sie vor dem Online-Abschluss die unterschriebenen Vertragsunterlagen und
        Informationen als PDF und prüfen Sie diese auf Richtigkeit.
      </Text>
      <AuxButton
        key="1"
        step={1}
        label="Unterlagen herunterladen"
        onClick={onDownloadPdfClick}
        iconRight={
          (isPdfDownloaded && CheckIcon) ||
          (!isPdfDownloaded && isLoadingPdf && LoaderIcon) ||
          undefined
        }
      />
      <AemFragment name={AemFragmentCatalog.STEP24VA} useVA={true} />
      <Text styles="bold subheader">Zahlungspflichtiger Vertrag</Text>
      <Text>Bei Vertragsabschluss fallen für Sie keine weiteren Kosten an.</Text>
      <Footer
        labelNext="Online beantragen"
        handleNext={() => {
          trackClickEvent(TrackingElementDUUPage.BUTTON_ONLINE_BEANTRAGEN);
          onSubmit('', NavigationAction.NEXT);
        }}
        handleAction={(navigationAction: NavigationAction) => {
          if (navigationAction === NavigationAction.BACK) {
            downloadPDFUtils.setPdfDownloaded(false);
            onNavigateNoSubmit(NavigationAction.BACK);
          }
        }}
        showNext={true}
        showPrevious={true}
        stepNext={2}
        disableNext={isSubmitOrderButtonDisabled}
      />

      <ErrorModal open={openErrorModal} businessId={businessId} onClick={onDownloadPdfClick} />
      <AppLoaderBlack
        show={isLoadingPdf}
        firstLine="Moment bitte ..."
        secondLine="Die Verarbeitung Ihrer Daten kann bis zu 20 Sekunden dauern."
      />
    </div>
  );
};
